import { Injectable, computed, signal } from '@angular/core';
import { Maintenance, MaintenanceStatus } from '../models/maintenance-request.model';
import { MaintenanceLog } from '../../../../shared/definitions/maintenance-log';
import type { IMaintenanceStore } from './maintenance-store.interface';

@Injectable({ providedIn: 'root' })
export class MaintenanceMockStore implements IMaintenanceStore {
  private readonly _maintenance = signal<readonly Maintenance[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly maintenance = this._maintenance.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._maintenance().length);
  readonly activeCount = computed(() =>
    this._maintenance().filter(m =>
      m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Canceled
    ).length
  );
  readonly completedCount = computed(() =>
    this._maintenance().filter(m => m.status === MaintenanceStatus.Completed).length
  );

  readonly counts = computed(() => {
    const out = { scheduled: 0, inProgress: 0, completed: 0, overdue: 0, canceled: 0, pending: 0, total: 0 };
    for (const m of this._maintenance()) {
      out.total++;
      switch (m.status) {
        case MaintenanceStatus.Scheduled: out.scheduled++; break;
        case MaintenanceStatus.InProgress: out.inProgress++; break;
        case MaintenanceStatus.Completed: out.completed++; break;
        case MaintenanceStatus.Overdue: out.overdue++; break;
        case MaintenanceStatus.Canceled: out.canceled++; break;
        case MaintenanceStatus.Pending: out.pending++; break;
      }
    }
    return out;
  });

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  getMaintenanceById(maintenanceUID: string): Maintenance | null {
    return this._maintenance().find(m => m.maintenanceUID === maintenanceUID) ?? null;
  }

  getMaintenanceByStatus(status: MaintenanceStatus): readonly Maintenance[] {
    return this._maintenance().filter(m => m.status === status);
  }

  getMaintenanceForProperty(propertyUID: string): readonly Maintenance[] {
    return this._maintenance().filter(m => m.propertyUID === propertyUID);
  }

  getMaintenanceForBuilding(buildingUID: string): readonly Maintenance[] {
    return this._maintenance().filter(m => m.buildingUID === buildingUID);
  }

  getActiveMaintenance(): readonly Maintenance[] {
    return this._maintenance().filter(m =>
      m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Canceled
    );
  }

  async addMaintenance(maintenance: Maintenance): Promise<string> {
    const uid = `mock-maint-${Math.random().toString(36).slice(2, 10)}`;
    const now = new Date();
    const next = Object.assign(new Maintenance(), maintenance, {
      maintenanceUID: uid,
      createdAt: now,
      updatedAt: now
    });
    this._maintenance.set([...this._maintenance(), next]);
    return uid;
  }

  async updateMaintenance(maintenance: Maintenance): Promise<void> {
    if (!maintenance.maintenanceUID) throw new Error('maintenanceUID required');
    const uid = maintenance.maintenanceUID;
    this._maintenance.set(
      this._maintenance().map(m =>
        m.maintenanceUID === uid
          ? Object.assign(new Maintenance(), m, maintenance, { updatedAt: new Date() })
          : m
      )
    );
  }

  async updateMaintenanceStatus(maintenanceUID: string, status: MaintenanceStatus, _note?: string): Promise<void> {
    const now = new Date();
    this._maintenance.set(
      this._maintenance().map(m => {
        if (m.maintenanceUID !== maintenanceUID) return m;
        const patch: Partial<Maintenance> = { status, updatedAt: now };
        if (status === MaintenanceStatus.Completed) patch.completedDate = now;
        return Object.assign(new Maintenance(), m, patch);
      })
    );
  }

  async deleteMaintenance(maintenanceUID: string): Promise<void> {
    this._maintenance.set(this._maintenance().filter(m => m.maintenanceUID !== maintenanceUID));
  }

  private readonly _logs = new Map<string, MaintenanceLog[]>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly _history = new Map<string, any[]>();

  async updateMaintenanceStatusWithLog(
    maintenanceUID: string,
    status: MaintenanceStatus,
    notes?: string,
    documentUIDs?: string[]
  ): Promise<void> {
    await this.updateMaintenanceStatus(maintenanceUID, status);
    await this.addGeneralLog(maintenanceUID, `Status Changed to ${status}`, notes, documentUIDs);
  }

  async addGeneralLog(
    maintenanceUID: string,
    action: string,
    notes?: string,
    documentUIDs?: string[]
  ): Promise<string> {
    const uid = `mock-mlog-${Math.random().toString(36).slice(2, 10)}`;
    const log = new MaintenanceLog(maintenanceUID, action, notes);
    log.logUID = uid;
    if (documentUIDs?.length) log.documentUIDs = documentUIDs;
    const existing = this._logs.get(maintenanceUID) ?? [];
    this._logs.set(maintenanceUID, [log, ...existing]);
    return uid;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getCompletionHistory(maintenanceUID: string): Promise<any[]> {
    return this._history.get(maintenanceUID) ?? [];
  }

  async getMaintenanceLogs(maintenanceUID: string): Promise<MaintenanceLog[]> {
    return [...(this._logs.get(maintenanceUID) ?? [])];
  }

  /** Test helper. */
  _seed(items: readonly Maintenance[]): void {
    this._maintenance.set(items);
  }
}
